<template>
  <DateInput
    ref="targetRef"
    :bs="bs"
    :readonly="readonly"
    :disabled="disabled"
    :is-open="isActive"
    :value="dayStrList.join(',')"
    v-bind="$attrs"
    @click:clear="() => handleClear()"
    @click="() => toggle()"
  >
    <template #custom>
      <BeerInputTag
        v-for="dayStr of dayStrList"
        :key="dayStr"
        :disabled="disabled"
        :readonly="readonly"
        @click:icon="() => (readonly || disabled) ? {} : {}"
      >
        {{ dayStr }}
      </BeerInputTag>
    </template>
    <BodyPortal :active="fixed">
      <Calendar
        ref="popperRef"
        :container-props="bindContainerProps"
        :base="base"
        @click.stop="() => {}"
      />
    </BodyPortal>
  </DateInput>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed } from 'vue'
  import { SelectModeType, ViewModeType } from '@xhs/yam-beer/cmp/constants'
  import type { Dayjs } from 'dayjs'
  import BeerInputTag from '@xhs/yam-beer/cmp/common/Tag/InputTag.vue'
  import { useSelectPopover, SelectPopoverPartial } from '../../../compose/popover'
  import DateInput from './DateInput.vue'
  import Calendar from './Calendar/Calendar.vue'
  import { useDateValueList, Model } from './dateUtils'
  import BodyPortal from '../../primitive/BodyPortal'

  /**
   * 多个日期选择
   */
  export default defineComponent({
    name: 'MultiDatePicker',
    components: {
      DateInput,
      Calendar,
      BeerInputTag,
      BodyPortal,
    },
    props: {
      /**
       * 选项 SelectButton 的 inline 样式，作为 inputProps 的快捷方式
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      modelValue: {
        type: [Object, String] as PropType<Model[]>,
      },
      /**
       * 禁止日期
       */
      disableDay: {
        type: Function,
      },
      /**
       * 是否需要选择日期
       * 选择模式，默认选择日期
       * day | month | year | time
       */
      selectMode: {
        type: String as PropType<SelectModeType>,
        default: 'day',
      },
      /**
       * 默认时间 支持字符串格式 "12:00:00"
       */
      defaultTime: {
        type: String,
        default: '12:00:00',
      },
      /**
       * 下拉框是否是 fixed 模式
       */
      fixed: {
        type: Boolean,
        default: false,
      },
      /**
       * 下拉框的高级选项
       */
      popoverParams: {
        type: Object as PropType<SelectPopoverPartial>,
        default: () => ({}),
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const {
        base,
        dayStateList,
        dayStrList,
        setModel,
      } = useDateValueList({
        modelRef: computed(() => props.modelValue),
        selectMode: props.selectMode,
        defaultTime: props.defaultTime,
      })

      const {
        targetRef,
        popperRef,
        isActive,
        update,
        toggle,
      } = useSelectPopover({
        strategy: props.fixed ? 'fixed' : 'absolute',
        ...props.popoverParams,
        disabled: computed(() => props.disabled || props.readonly),
        sameWidth: false,
      })

      const handleClick = (obj: Dayjs|null) => {
        const val = setModel(obj)
        emit('update:modelValue', val)
        update()
      }

      const handleClear = () => {
        handleClick(null)
      }

      return {
        targetRef,
        popperRef,
        isActive,
        toggle,

        base,
        dayStrList,
        handleClick,
        handleClear,
        bindContainerProps: computed(() => {
          let bindProps = {}
          if (props.selectMode === 'year') {
            bindProps = {
              defaultMode: 'y',
              onSetViewMode: (mode: ViewModeType, baseObj: Dayjs) => {
                if (mode === 'm') {
                  handleClick(baseObj)
                  return 'year'
                }
                return mode
              },
            }
          } else if (props.selectMode === 'month') {
            bindProps = {
              defaultMode: 'm',
              onSetViewMode: (mode: ViewModeType, baseObj: Dayjs) => {
                if (mode === 'd') {
                  handleClick(baseObj)
                  return 'm'
                }
                return mode
              },
            }
          }
          return {
            ...bindProps,
            disableDay: props.disableDay,
            onClickDay: handleClick,
            selected: dayStateList.value,
          }
        }),
      }
    },
  })
</script>
